import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, HostListener } from '@angular/core';
import { FilterState } from '@ui/shared/domain-filter';
import { FilterDataStatus, FilterModel, FilterOption } from '@ui/shared/util-filter';
import { OverlayListenerOptions, OverlayOptions } from 'primeng/api';
import { Observable, distinctUntilChanged, map } from 'rxjs';
import { FilterElementComponent } from '../filter-element/filter-element.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ui-base-filter',
    template: '',
    styleUrls: [],
    standalone: true,
})
export abstract class BaseFilterComponent extends FilterElementComponent {
    @Input({ required: true })
    public filter!: FilterModel;

    @Input()
    public placeholder = 'Bitte Wählen';

    @Output()
    public readonly filterValueChanged = new EventEmitter<FilterModel>();

    public loadingStatus = FilterDataStatus.Loading;
    public overlayOptions: OverlayOptions;

    public readonly values$: Observable<Array<FilterOption>>;

    private _previousScreenWidth: number = window.innerWidth;

    constructor(
        private readonly _activeFilterService: FilterState,
    ) {
        super();

        this.values$ = _activeFilterService.activeFilters$
            .pipe(
                map(activeFilters => {
                    const matchingValue = activeFilters.get(this.filter.key);

                    return matchingValue?.values ?? [];
                }),
                distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
            );

        this.overlayOptions = {
            listener: (_event: Event, options: OverlayListenerOptions | undefined) => {
                if (options?.type === 'resize') {

                    /*
                     * In mobile browsers, opening a filter overlay can cause the on-screen keyboard to appear,
                     * which resizes the browser screen and in turn closes the overlay again.
                     * To prevent this behaviour and still close the overlay on orientation or desktop/mobile changes,
                     * it is only closed if the screen width has changed.
                     */
                    if (window.innerWidth === this._previousScreenWidth) {
                        return false;
                    } else {
                        return true;
                    }
                }

                // Don't close the overlay on scroll. This would also close the overlay on mobile when the on-screen keyboard opens.
                if (options?.type === 'scroll') {
                    return false;
                }

                return true;
            },
        };
    }

    /**
     * Whenever the screen resizes, track the screen width for use in overlay events.
     * The value is only saved after a timeout, so to not interfere with the overlay events while they use it.
     */
    @HostListener('window:resize')
    public onResize(): void {
        const screenWidthTrackingDelay = 100;

        setTimeout(
            () => { this._previousScreenWidth = window.innerWidth; },
            screenWidthTrackingDelay,
        );
    }

    public onValueChange(newValues: Array<FilterOption> | null): void {

        /*
         * this new filter does not have impact on the api in InjectableMapDataStateService.
         * the filter value are extracted from the definition and not the filters
         */
        const filter = new FilterModel(this.filter.key, this.filter.title, newValues ?? []);

        this._activeFilterService.setActiveFilter(filter);
    }
}
